import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Check, Gift, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  OrderCreate,
  OrderDetailCreate,
  OrderType,
  OrderStatus,
  PaymentMethod,
} from "@cold-crew/shared";
import {
  OrderDetailForm,
  type OrderProductSummary,
} from "./OrderDetailForm";

interface OrderFormProps {
  onSubmit: (data: OrderCreate) => void | Promise<void>;
  isLoading?: boolean;
  submitLabel?: string;
  initialValues?: Partial<OrderCreate>;
}

const inputCls = cn(
  "h-11 w-full min-w-0 rounded-lg border border-[#ddd8cf] bg-[#fbfaf7] px-3",
  "text-[14px] font-medium text-[#282622] placeholder:text-[#aaa59c]",
  "transition-colors focus:border-[#f47b2a] focus:outline-none focus:ring-2 focus:ring-[#f47b2a]/15"
);

const labelCls = "text-[13px] font-bold text-[#3d3934]";
const today = () => new Date().toISOString().slice(0, 10);

const ENTREGA: { value: OrderStatus; label: string }[] = [
  { value: "pendiente", label: "Pendiente" },
  { value: "entregado", label: "Entregado" },
  { value: "cancelado", label: "Cancelado" },
];

const METODOS: { value: PaymentMethod; label: string }[] = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "otro", label: "Otro" },
  { value: "n/a", label: "N/A" },
];

function money(value: number) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function SectionTitle({
  number,
  children,
}: {
  number: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center gap-2.5">
      <span className="h-[2px] w-4 rounded-full bg-[#f47b2a]" />
      <span className="text-[11px] font-black tracking-[0.16em] text-[#9a958d]">
        {number}
      </span>
      <h2 className="text-[16px] font-black text-[#171614]">{children}</h2>
    </div>
  );
}

export function OrderForm({
  onSubmit,
  isLoading = false,
  submitLabel = "Guardar",
  initialValues,
}: OrderFormProps) {
  const navigate = useNavigate();

  const [form, setForm] = useState<OrderCreate>({
    cliente_nombre: initialValues?.cliente_nombre ?? "",
    cliente_contacto: initialValues?.cliente_contacto ?? "",
    fecha_pedido: initialValues?.fecha_pedido ?? today(),
    fecha_entrega: initialValues?.fecha_entrega ?? today(),
    status: initialValues?.status ?? "pendiente",
    pagado: initialValues?.pagado ?? false,
    tipo_pedido: initialValues?.tipo_pedido ?? "venta",
    metodo_pago: initialValues?.metodo_pago ?? "efectivo",
    descuento: initialValues?.descuento ?? 0,
    notas: initialValues?.notas ?? "",
    detalles: initialValues?.detalles ?? [],
  });

  const [error, setError] = useState<string | null>(null);
  const [productSummary, setProductSummary] = useState<OrderProductSummary[]>([]);

  const esRegalo = form.tipo_pedido === "regalo";

  function set<K extends keyof OrderCreate>(key: K, val: OrderCreate[K]) {
    setForm((current) => ({ ...current, [key]: val }));
  }

  function handleTipoPedido(tipo: OrderType) {
    setForm((current) => ({
      ...current,
      tipo_pedido: tipo,
      ...(tipo === "regalo"
        ? { pagado: false, metodo_pago: "n/a" as PaymentMethod }
        : current.metodo_pago === "n/a"
          ? { metodo_pago: "efectivo" as PaymentMethod }
          : {}),
    }));
  }

  function setDetalles(detalles: OrderDetailCreate[]) {
    set("detalles", detalles);
  }

  const subtotal = form.detalles.reduce(
    (sum, line) =>
      sum + (line.cantidad || 0) * (line.precio_unitario || 0),
    0
  );
  const total = Math.max(0, subtotal - (form.descuento || 0));

  async function handleSubmit() {
    if (!form.cliente_nombre.trim()) {
      setError("El nombre del cliente es obligatorio.");
      return;
    }

    const lineas = form.detalles.filter(
      (line) => line.producto_id && line.cantidad > 0
    );

    if (lineas.length === 0) {
      setError("Agrega al menos un producto.");
      return;
    }

    setError(null);

    const payload: OrderCreate = {
      cliente_nombre: form.cliente_nombre.trim(),
      cliente_contacto: form.cliente_contacto?.trim() || undefined,
      fecha_pedido: form.fecha_pedido,
      fecha_entrega: form.fecha_entrega || undefined,
      status: form.status,
      pagado: esRegalo ? false : (form.pagado ?? false),
      tipo_pedido: form.tipo_pedido,
      metodo_pago: esRegalo ? "n/a" : form.metodo_pago,
      descuento: form.descuento || 0,
      notas: form.notas?.trim() || undefined,
      detalles: lineas,
    };

    await onSubmit(payload);
  }

  return (
    <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_310px] xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="grid gap-4 md:grid-cols-2">
        <section className="min-h-[188px] rounded-2xl border border-[#ded9cf] bg-white p-6">
          <SectionTitle number="01">Tipo de pedido</SectionTitle>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={() => handleTipoPedido("venta")}
              className={cn(
                "flex h-[96px] items-center gap-3 rounded-xl border px-4 text-left transition-all",
                form.tipo_pedido === "venta"
                  ? "border-[#171614] bg-[#171614] text-white"
                  : "border-[#ddd8cf] bg-white text-[#171614] hover:border-[#aaa49a]"
              )}
            >
              <span className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-lg",
                form.tipo_pedido === "venta" ? "bg-[#f47b2a] text-[#171614]" : "bg-[#f1eee8]"
              )}>
                <ShoppingBag className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[16px] font-black">Venta</span>
                <span className={cn(
                  "mt-0.5 block text-[11px] leading-tight",
                  form.tipo_pedido === "venta" ? "text-white/60" : "text-[#8b867e]"
                )}>
                  Cuenta como ingreso
                </span>
              </span>
            </button>

            <button
              type="button"
              disabled={isLoading}
              onClick={() => handleTipoPedido("regalo")}
              className={cn(
                "flex h-[96px] items-center gap-3 rounded-xl border px-4 text-left transition-all",
                form.tipo_pedido === "regalo"
                  ? "border-[#171614] bg-[#171614] text-white"
                  : "border-[#ddd8cf] bg-white text-[#171614] hover:border-[#aaa49a]"
              )}
            >
              <span className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-lg",
                form.tipo_pedido === "regalo" ? "bg-[#f47b2a] text-[#171614]" : "bg-[#f1eee8]"
              )}>
                <Gift className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[16px] font-black">Regalo</span>
                <span className={cn(
                  "mt-0.5 block text-[11px] leading-tight",
                  form.tipo_pedido === "regalo" ? "text-white/60" : "text-[#8b867e]"
                )}>
                  Sin cobro
                </span>
              </span>
            </button>
          </div>
        </section>

        <section className="min-h-[188px] rounded-2xl border border-[#ded9cf] bg-white p-6">
          <SectionTitle number="02">Cliente</SectionTitle>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className={labelCls}>Nombre del cliente *</label>
              <input
                className={inputCls}
                value={form.cliente_nombre}
                disabled={isLoading}
                placeholder="Ej. Maria Lopez"
                onChange={(e) => set("cliente_nombre", e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Contacto</label>
              <input
                className={inputCls}
                value={form.cliente_contacto ?? ""}
                disabled={isLoading}
                placeholder="Teléfono o correo"
                onChange={(e) => set("cliente_contacto", e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="min-h-[242px] rounded-2xl border border-[#ded9cf] bg-white p-6 md:col-span-2">
          <SectionTitle number="03">Productos</SectionTitle>
          <OrderDetailForm
            value={form.detalles}
            onChange={setDetalles}
            disabled={isLoading}
            onSummaryChange={setProductSummary}
          />
        </section>

        <section className="min-h-[276px] rounded-2xl border border-[#ded9cf] bg-white p-6 md:col-span-2">
          <SectionTitle number="04">Entrega y pago</SectionTitle>

          <div className="grid grid-cols-2 gap-x-3 gap-y-5 md:grid-cols-4">
            <div className="space-y-1">
              <label className={labelCls}>Fecha pedido *</label>
              <input
                type="date"
                className={inputCls}
                value={form.fecha_pedido}
                disabled={isLoading}
                onChange={(e) => set("fecha_pedido", e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Fecha entrega</label>
              <input
                type="date"
                className={inputCls}
                value={form.fecha_entrega ?? ""}
                disabled={isLoading}
                onChange={(e) => set("fecha_entrega", e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Estado</label>
              <select
                className={inputCls}
                value={form.status}
                disabled={isLoading}
                onChange={(e) => set("status", e.target.value as OrderStatus)}
              >
                {ENTREGA.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Pagado</label>
              <button
                type="button"
                disabled={isLoading || esRegalo}
                onClick={() => set("pagado", !form.pagado)}
                className={cn(
                  "flex h-11 w-full items-center justify-between rounded-lg border border-[#ddd8cf] bg-[#fbfaf7] px-3",
                  esRegalo && "cursor-not-allowed opacity-50"
                )}
              >
                <span className="text-[13px] font-semibold text-[#6f6a62]">
                  {esRegalo ? "N/A" : form.pagado ? "Sí" : "No"}
                </span>
                <span className={cn(
                  "relative h-5 w-9 rounded-full transition-colors",
                  !esRegalo && form.pagado ? "bg-[#171614]" : "bg-[#dedbd3]"
                )}>
                  <span className={cn(
                    "absolute top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform",
                    !esRegalo && form.pagado ? "translate-x-[18px]" : "translate-x-0.5"
                  )} />
                </span>
              </button>
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Método de pago</label>
              <select
                className={cn(inputCls, esRegalo && "cursor-not-allowed opacity-50")}
                value={esRegalo ? "n/a" : form.metodo_pago}
                disabled={isLoading || esRegalo}
                onChange={(e) => set("metodo_pago", e.target.value as PaymentMethod)}
              >
                {METODOS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className={labelCls}>Descuento</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-[#8b867e]">$</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className={cn(inputCls, "pl-7")}
                  value={form.descuento || ""}
                  disabled={isLoading}
                  placeholder="0.00"
                  onChange={(e) => set("descuento", Number(e.target.value))}
                />
              </div>
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className={labelCls}>Notas</label>
              <input
                className={inputCls}
                value={form.notas ?? ""}
                disabled={isLoading}
                placeholder="Observaciones (opcional)"
                onChange={(e) => set("notas", e.target.value)}
              />
            </div>
          </div>
        </section>
      </div>

      <aside className="lg:sticky lg:top-3">
        <div className="min-h-[635px] overflow-hidden rounded-2xl border border-[#ded9cf] bg-white">
          <div className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <img
                  src="/logo_sin_fondo.png"
                  alt="Cold Crew"
                  className="h-9 w-auto shrink-0 object-contain"
                />
                <div className="min-w-0">
                  <h2 className="text-[17px] font-black leading-tight text-[#171614]">
                    COLD CREW
                  </h2>
                  <p className="text-[12px] font-medium text-[#8b867e]">
                    Resumen del pedido
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-[#171614] px-2 py-1 text-[11px] font-bold text-white">
                {esRegalo ? "Regalo" : "Venta"}
              </span>
              <span className="rounded-full bg-[#efede8] px-2 py-1 text-[11px] font-bold text-[#615d56]">
                {ENTREGA.find((item) => item.value === form.status)?.label}
              </span>
              {!esRegalo && (
                <span className={cn(
                  "rounded-full px-2 py-1 text-[11px] font-bold",
                  form.pagado
                    ? "bg-[#dff3e8] text-[#176c4f]"
                    : "bg-[#ffe2cd] text-[#9a4816]"
                )}>
                  {form.pagado ? "Pagado" : "No pagado"}
                </span>
              )}
            </div>

            <div className="my-3 border-t border-dashed border-[#ddd8cf]" />

            <p className="text-[11px] font-black uppercase tracking-[0.13em] text-[#99948b]">
              Cliente
            </p>
            <p className="mt-1.5 truncate text-[17px] font-black text-[#171614]">
              {form.cliente_nombre.trim() || "Sin cliente"}
            </p>

            <div className="my-3 border-t border-dashed border-[#ddd8cf]" />

            <div className="min-h-[52px] space-y-1.5">
              {productSummary.length === 0 ? (
                <p className="py-3 text-center text-[13px] font-medium text-[#9a958d]">
                  Aún no hay productos
                </p>
              ) : (
                productSummary.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 text-[13px]">
                    <span className="min-w-0 truncate font-semibold text-[#57534d]">
                      {item.cantidad} × {item.nombre}
                    </span>
                    <span className="shrink-0 font-black tabular-nums text-[#171614]">
                      {money(item.cantidad * item.precio_unitario)}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="my-3 border-t border-dashed border-[#ddd8cf]" />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[13px]">
                <span className="font-medium text-[#77726a]">Subtotal</span>
                <span className="font-bold tabular-nums text-[#171614]">{money(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <span className="font-medium text-[#77726a]">Descuento</span>
                <span className="font-bold tabular-nums text-[#171614]">-{money(form.descuento || 0)}</span>
              </div>
              <div className="mt-2 flex items-end justify-between border-t border-[#eeeae2] pt-2.5">
                <span className="text-[15px] font-black text-[#171614]">Total</span>
                <span className="text-[34px] font-black leading-none tracking-[-0.05em] tabular-nums text-[#171614]">
                  {money(total)}
                </span>
              </div>
            </div>

            <div className="relative mt-4 flex h-[165px] items-center justify-center overflow-hidden">
              <div className="absolute h-[100px] w-[230px] rounded-[50%] bg-[#f4eee5]" />
              <img
                src="/cold-crew-mascot.png"
                alt=""
                aria-hidden="true"
                className="relative z-[1] h-[155px] w-auto object-contain"
              />
            </div>

            {error && (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-[10px] font-semibold text-red-700">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#f47b2a] px-4 text-[14px] font-black text-[#171614] transition-colors hover:bg-[#ea6e1d] disabled:opacity-50"
            >
              <Check className="size-4" strokeWidth={2.6} />
              {isLoading ? "Guardando..." : submitLabel}
            </button>

            <button
              type="button"
              disabled={isLoading}
              onClick={() => navigate({ to: "/pedidos" })}
              className="mt-2 h-9 w-full text-[12px] font-bold text-[#77726a] transition-colors hover:text-[#171614]"
            >
              Cancelar
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
